<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="cm-toolbar shrink-0">
      <h2>Decision Ledger</h2>
      <span class="text-xs text-gray-400 ml-2">what rounds decided vs. what actually happened</span>
      <div class="flex-1"></div>
      <div class="cm-seg">
        <button :class="{ on: scope === 'open' }" @click="scope = 'open'">Open</button>
        <button :class="{ on: scope === 'done' }" @click="scope = 'done'">Completed</button>
        <button :class="{ on: scope === 'all' }" @click="scope = 'all'">All</button>
      </div>
      <input
        v-model="search"
        class="cm-input cm-input-sm w-56"
        placeholder="Patient, cause, or to-do..."
      />
      <button class="cm-btn cm-btn-sm cm-btn-secondary" @click="loadLedger">Refresh</button>
    </div>

    <div class="flex-1 overflow-y-auto min-h-0">
      <table class="cm-table">
        <thead>
          <tr>
            <th>Patient</th>
            <th>Cause</th>
            <th>Decision</th>
            <th>Pathway</th>
            <th>Owner</th>
            <th>Decided</th>
            <th class="num">Age</th>
            <th class="ctr">Status</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading">
            <tr v-for="n in 10" :key="'sk' + n">
              <td v-for="c in 8" :key="c">
                <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: (40 + (c * 13) % 50) + '%' }"></div>
              </td>
            </tr>
          </template>
          <tr v-else-if="!filteredRows.length">
            <td colspan="8">
              <div class="cm-empty">
                <div class="msg">No rounds decisions recorded yet.</div>
              </div>
            </td>
          </tr>
          <tr v-for="row in filteredRows" v-else :key="row.task_id">
            <td>
              <span class="strong">{{ titleCase(row.patient_name) }}</span>
              <span class="cm-mono text-gray-400 ml-1.5">{{ row.patient_identifier }}</span>
            </td>
            <td class="whitespace-normal max-w-[220px]">{{ row.signal_title }}</td>
            <td class="whitespace-normal max-w-[280px]"><span class="strong">{{ row.task_title }}</span></td>
            <td>
              <span v-if="row.readmission_pathway" class="cm-badge brand">{{ row.readmission_pathway }}</span>
              <span v-else class="dim">—</span>
            </td>
            <td>
              <span v-if="row.owner_name">{{ row.owner_name }}</span>
              <span v-else class="cm-badge warn">Unassigned</span>
            </td>
            <td>{{ shortDate(row.created_at) }}</td>
            <td class="num" :class="{ 'text-red-600 font-medium': statusOf(row) === 'open' && ageDays(row) > 30 }">
              {{ ageDays(row) }}d
            </td>
            <td class="ctr">
              <span v-if="statusOf(row) === 'done'" class="cm-badge ok">
                Done<span v-if="row.closure_grade === 'verified_terminal'"> ✓</span>
              </span>
              <span v-else-if="statusOf(row) === 'unable'" class="cm-badge bad">Unable</span>
              <span v-else-if="row.task_status === 'attempted'" class="cm-badge info">In Progress</span>
              <span v-else class="cm-badge">Open</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UMLedger',
  data() {
    return {
      loading: true,
      rows: [],
      scope: 'open',
      search: '',
    };
  },
  computed: {
    filteredRows() {
      const q = this.search.trim().toLowerCase();
      return this.rows.filter(r => {
        const s = this.statusOf(r);
        if (this.scope === 'open' && (s === 'done' || s === 'unable')) return false;
        if (this.scope === 'done' && s !== 'done' && s !== 'unable') return false;
        if (!q) return true;
        return (r.patient_name || '').toLowerCase().includes(q)
          || (r.signal_title || '').toLowerCase().includes(q)
          || (r.task_title || '').toLowerCase().includes(q);
      });
    },
  },
  mounted() {
    this.loadLedger();
  },
  methods: {
    async loadLedger() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get(
          '/api/careplannodes/todo/',
          { params: { source_work_type: 'um_rounds_presentation', include_resolved: '1' } },
        );
        if (data?.success) this.rows = data.rows || [];
      } catch (e) {
        console.error('Error loading UM ledger:', e);
      } finally {
        this.loading = false;
      }
    },
    statusOf(row) {
      if (row.resolution_outcome === 'done') return 'done';
      if (row.resolution_outcome === 'unable_to_complete') return 'unable';
      return 'open';
    },
    ageDays(row) {
      if (!row.created_at) return 0;
      return Math.max(0, Math.round((Date.now() - new Date(row.created_at).getTime()) / 86400000));
    },
    titleCase(s) {
      return (s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    shortDate(v) {
      if (!v) return '—';
      const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return String(v);
      return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
    },
  },
};
</script>
